import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Snackbar,
    Alert,
    CircularProgress as MuiCircularProgress,
    LinearProgress,
    Collapse,
    IconButton,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import config from '../config';
import axios from "axios";

const CompletionCircle = ({ value }) => {
    return (
        <Box position="relative" display="inline-flex">
            <MuiCircularProgress
                variant="determinate"
                value={value}
                size={40}
                thickness={4}
                sx={{
                    color: value > 70
                        ? '#4caf50'
                        : value > 40
                            ? '#ff9800'
                            : '#f44336',
                }}
            />
            <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                >
                    {`${Math.round(value)}%`}
                </Typography>
            </Box>
        </Box>
    );
};

const TypeGroup = ({ type, children, open, onToggle }) => {
    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset', bgcolor: '#f5f5f5' } }}>
                <TableCell colSpan={6}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={onToggle}
                        sx={{ mr: 1 }}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {type}
                    </Typography>
                </TableCell>
            </TableRow>
            {children}
        </>
    );
};

const Zapros = () => {
    const [ous, setOus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [progress, setProgress] = useState({});
    const [isChecking, setIsChecking] = useState({});
    const [completionPercentages, setCompletionPercentages] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    const extractNumber = (str) => {
        const match = str?.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
    };

    const connectToSSE = (ouId, link) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Требуется авторизация');
            return null;
        }

        const eventSource = new EventSource(`${config.apiBaseUrl}/admin/check-website?url=${encodeURIComponent(link)}&token=${token}&ouId=${ouId}`);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.progress !== undefined) {
                    setProgress(prev => ({ ...prev, [ouId]: data.progress }));
                }
                if (data.message) {
                    setSnackbarMessage(data.message);
                    setOpenSnackbar(true);
                }
                if (data.success) {
                    setIsChecking(prev => ({ ...prev, [ouId]: false }));
                    if (data.completionPercentage !== undefined) {
                        setCompletionPercentages(prev => ({
                            ...prev,
                            [ouId]: data.completionPercentage
                        }));
                    }
                }
            } catch (err) {
                console.error('Ошибка обработки SSE сообщения:', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE ошибка:', err);
            eventSource.close();
            setIsChecking(prev => ({ ...prev, [ouId]: false }));
        };

        return eventSource;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Требуется авторизация');
            }

            const [linksResponse, completionResponse] = await Promise.all([
                axios.get(`${config.apiBaseUrl}/admin/ou-links`, {
                    headers: { 'x-access-token': token },
                }),
                axios.get(`${config.apiBaseUrl}/admin/completionpercentages`, {
                    headers: { 'x-access-token': token },
                })
            ]);

            // Сортируем ОУ по типу и номеру
            const sortedOus = linksResponse.data.sort((a, b) => {
                if (a.type === b.type) {
                    return extractNumber(a.number) - extractNumber(b.number);
                }
                return a.type.localeCompare(b.type);
            });

            setOus(sortedOus);

            const percentages = {};
            completionResponse.data.forEach(item => {
                percentages[item.ou_id] = item.completion_percentage;
            });
            setCompletionPercentages(percentages);

            // Загружаем прогресс проверки
            try {
                const progressResponse = await axios.get(`${config.apiBaseUrl}/admin/check-progress`, {
                    headers: { 'x-access-token': token },
                });
                setProgress(progressResponse.data.progress || {});
                setIsChecking(progressResponse.data.isChecking || {});

                // Восстанавливаем SSE соединения для активных проверок
                for (const [ouId, isActive] of Object.entries(progressResponse.data.isChecking || {})) {
                    if (isActive) {
                        const ou = sortedOus.find((ou) => ou.id === ouId);
                        if (ou) {
                            connectToSSE(ouId, ou.link);
                        }
                    }
                }
            } catch (progressErr) {
                console.error('Ошибка загрузки прогресса:', progressErr);
                setProgress({});
                setIsChecking({});
            }

            // Инициализируем все группы как развернутые
            const groups = {};
            sortedOus.forEach(ou => {
                groups[ou.type] = true;
            });
            setExpandedGroups(groups);

        } catch (err) {
            console.error('Ошибка при загрузке данных:', err);
            setError(err.message || 'Ошибка при загрузке данных');
            setOus([]);
            setCompletionPercentages({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCheckWebsite = async (ou) => {
        const { id, link } = ou;
        setIsChecking((prev) => ({ ...prev, [id]: true }));
        setProgress((prev) => ({ ...prev, [id]: undefined }));
        connectToSSE(id, link);
    };

    const handleCheckAllWebsites = async () => {
        for (const ou of ous) {
            await handleCheckWebsite(ou);
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const toggleGroup = (type) => {
        setExpandedGroups(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const groupedOus = ous.reduce((acc, ou) => {
        if (!acc[ou.type]) {
            acc[ou.type] = [];
        }
        acc[ou.type].push(ou);
        return acc;
    }, {});

    const handleRetry = () => {
        fetchData();
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box mt={5} textAlign="center">
                    <MuiCircularProgress />
                    <Typography variant="h6">Загрузка данных...</Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Box mt={5}>
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={handleRetry}>
                                Повторить
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box mt={5} textAlign="center">
                <Typography variant="h4" gutterBottom>
                    Запрос данных от сайтов ОУ
                </Typography>
            </Box>

            <Box mb={2} textAlign="center">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCheckAllWebsites}
                    disabled={Object.values(isChecking).some((checking) => checking)}
                >
                    Запустить все проверки
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>№ ОУ</TableCell>
                            <TableCell>Название</TableCell>
                            <TableCell>Ссылка на сайт</TableCell>
                            <TableCell align="center">Заполненность</TableCell>
                            <TableCell>Комментарии</TableCell>
                            <TableCell>Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(groupedOus).map(([type, ousInGroup]) => (
                            <React.Fragment key={type}>
                                <TypeGroup
                                    type={type}
                                    open={expandedGroups[type]}
                                    onToggle={() => toggleGroup(type)}
                                />
                                <TableRow>
                                    <TableCell style={{ padding: 0 }} colSpan={6}>
                                        <Collapse in={expandedGroups[type]} timeout="auto" unmountOnExit>
                                            <Table size="small" aria-label="purchases">
                                                <TableBody>
                                                    {ousInGroup.map((ou) => (
                                                        <TableRow key={ou.id}>
                                                            <TableCell>{ou.number}</TableCell>
                                                            <TableCell>{ou.name || '-'}</TableCell>
                                                            <TableCell>
                                                                <a href={ou.link} target="_blank" rel="noopener noreferrer">
                                                                    {ou.link}
                                                                </a>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <CompletionCircle value={completionPercentages[ou.id] || 0} />
                                                            </TableCell>
                                                            <TableCell>{ou.comments || '-'}</TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => handleCheckWebsite(ou)}
                                                                    disabled={isChecking[ou.id]}
                                                                >
                                                                    Проверить
                                                                </Button>
                                                                {isChecking[ou.id] && (progress[ou.id] ?? -1) >= 0 && (
                                                                    <Box mt={1}>
                                                                        <LinearProgress variant="determinate" value={progress[ou.id]} />
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="success">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Zapros;